import imageCompression from 'browser-image-compression';

// The custom avatar is a small square. 200x200 stays crisp on the largest tile
// (which scales the avatar up) while compressing to only a few KB — important
// because the resulting data URI is persisted locally in user-settings AND
// broadcast to every peer via ODIN userData on each profile flush.
export const AVATAR_PX = 200;

// Reject obviously-too-large source files before we spend time decoding them.
const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export interface CropRect {
  x: number;
  y: number;
  size: number;
}

export function isAcceptableSource(file: File): boolean {
  return file.type.startsWith('image/') && file.size > 0 && file.size <= MAX_SOURCE_BYTES;
}

// Loads a File into a decoded HTMLImageElement via an object URL. The caller
// owns the returned `objectUrl` and must revoke it once the element is no
// longer needed (the cropper keeps it alive while the user adjusts the crop).
export function loadImage(file: File): Promise<{ image: HTMLImageElement; objectUrl: string }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({ image, objectUrl });
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load image'));
    };
    image.src = objectUrl;
  });
}

function canEncodeWebp(): boolean {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  return canvas.toDataURL('image/webp').startsWith('data:image/webp');
}

// Draws the chosen square region of `image` into a 200x200 canvas, then
// re-encodes it small (WebP when the browser can encode it, otherwise JPEG)
// and returns it as a data: URI. Object URLs are same-origin, so the canvas is
// never tainted and `toBlob` succeeds.
export async function cropToAvatarDataUrl(
  image: HTMLImageElement,
  crop: CropRect
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_PX;
  canvas.height = AVATAR_PX;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, crop.x, crop.y, crop.size, crop.size, 0, 0, AVATAR_PX, AVATAR_PX);

  const type = canEncodeWebp() ? 'image/webp' : 'image/jpeg';
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Encoding failed'))), type, 0.82)
  );

  // Second pass enforces a hard size ceiling regardless of source entropy, so a
  // busy photo can't bloat userData. Already 200x200, so this only re-encodes.
  const file = new File([blob], 'avatar', { type });
  // Main thread on purpose: the library's worker importScripts() itself from a
  // CDN at runtime. A 200x200 re-encode is far too cheap to justify that.
  const compressed = await imageCompression(file, {
    maxWidthOrHeight: AVATAR_PX,
    maxSizeMB: 0.03,
    fileType: type,
    initialQuality: 0.82,
    useWebWorker: false,
  });
  return imageCompression.getDataUrlFromFile(compressed);
}
